/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { Box, Button, Stack } from "@mui/material";
import { useSortableComponentList } from "cms/components/hooks/use-sortable-component-list";
import { SVGIcons } from "cms/icons/svg-icons";
import { AddLineWithIcon } from "components/add-line-with-icon";
import LabelledField from "components/labelled-field";
import PositionButtons from "components/position-buttons";
import RemoveIcon from "components/remove-icon";
import { SortableContent } from "components/sortable-content";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { isArrayNotEmpty, isArrayGreaterThan } from "utils/empty-exists";
import { ComponentFeatureWebpageContext } from "./component-feature-webpage-context";

const defaultWebpage = Object.freeze({
    webpage: "",
    title: "",
    description: "",
    image: "",
});

/**
 * @param {object} props
 * @param {FeaturedWebpage[]} props.webpages
 * @param {(webpages: FeaturedWebpage[]) => void} props.onChangeList
 */
const FeatureWebpageComponentEditWrapper = (props) => {
    const { t } = useTranslation();
    const { webpages: initialWebpages = [], onChangeList, style } = props;

    if (typeof onChangeList !== "function") {
        throw Error("setLocalState was not supplied, or is not a function");
    }

    const {
        list,
        sortableMethods,
        controls,
    } = useSortableComponentList(
        initialWebpages,
        defaultWebpage,
        onChangeList
    );

    const { addNewItem, addNewItemBelow, removeItem, updateItems } = controls;
    const disableButton = isArrayGreaterThan(list,2);

    return (
        <Fragment>
            <LabelledField
                label={t("commons.webpageCards")}
                remarks={`(${t("commons.3cardsMax")})`}
                style={{ marginTop: 12 }}
            >
                <Box style={{ paddingTop: 6, display: "flex", gap: 12 }}>
                    <Button
                        size='medium'
                        variant='contained'
                        color={"primary"}
                        disabled={disableButton}
                        target='warning'
                        onClick={addNewItem}
                    >
                        <SVGIcons.common.Create style={{ paddingRight: 4, cursor: "pointer" }} /> {t("commons.addCard")}
                    </Button>
                </Box>
            </LabelledField>
            <div className='simplicity-image-gallery'>
                <Stack direction='column'>
                    {isArrayNotEmpty(list) && list.map((item, index) => {
                        const { guid } = item;
                        const ctx = {
                            list,
                            guid,
                            item,
                            index,
                            style,
                            position: index,
                            addNewItem,
                            updateItems,
                            removeItem,
                        };
                        return (
                            <ComponentFeatureWebpageContext.Provider key={`webpage-${guid}`} value={ctx}>
                                <SortableContent
                                    id={guid}
                                    index={index}
                                    sortableMethods={sortableMethods}
                                >
                                    {props.children}
                                    <div>
                                        <PositionButtons
                                            index={index}
                                            list={list}
                                            handleUpdate={updateItems}
                                        />
                                        <RemoveIcon
                                            className='simplicity-composer-field-editor-remove'
                                            onDeleteItem={() => removeItem(index)}
                                            index={index}
                                        />
                                    </div>
                                </SortableContent>
                                {list.length < 3 &&
                                    <AddLineWithIcon
                                        addNewItem={() => addNewItemBelow(index)}
                                    />
                                }
                            </ComponentFeatureWebpageContext.Provider>
                        );
                    })
                    }
                </Stack>
            </div>
        </Fragment>
    );
};
FeatureWebpageComponentEditWrapper.displayName = "FeatureWebpageComponentEditWrapper";
export { FeatureWebpageComponentEditWrapper };


/**
 * @typedef {object} FeaturedWebpage
 * @property {string} guid
 * @property {string} description
 * @property {string} image
 * @property {string} title
 * @property {string} webpage
 * @property {number} position
 */
